import { get as getPathValue, noop, set as setPathValue } from "lodash";
import type { StateCreator } from "zustand";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { defaultBlocks } from "@circle-react-shared/BuilderUI/DefaultBlocks";
import {
  createBlockEditorMapping,
  createBlockMetaMapping,
  createBlockRendererMapping,
} from "@circle-react-shared/BuilderUI/Helpers/blockMappings";
import {
  getBlockPathInfo,
  getParentArray,
} from "@circle-react-shared/BuilderUI/Helpers/blockPath";
import { cloneWithNewIDs } from "@circle-react-shared/BuilderUI/Helpers/cloneWithNewIDs";
import {
  isBaseBlock,
  isBlockArray,
  isChildrenProp,
} from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import type { Middlewares, Mutators } from "./index";
import type { BlocksState, BuilderUIState } from "./types";

export type BlocksSlice = StateCreator<
  BuilderUIState,
  Middlewares,
  Mutators,
  BlocksState
>;

export const createBlocksSlice: BlocksSlice = (set, get) => ({
  blockMapping: defaultBlocks,
  selectedBlockPath: null,
  draggingTemplate: null,

  onSave: noop,
  getBlockEditors: () => createBlockEditorMapping(get().blockMapping),
  getBlockRenderers: () => createBlockRendererMapping(get().blockMapping),
  getBlockMetaMapping: () => createBlockMetaMapping(get().blockMapping),
  getBlockMeta: (blockType: string) => {
    const blockMetaMapping = get().getBlockMetaMapping();
    return blockMetaMapping[blockType] ?? null;
  },

  addBlocks: (block: BaseBlock | BaseBlock[], path: string, index?: number) => {
    const blocks = get().getBlocks();
    const { updateBlocksFromCurrentPage } = get();

    const newBlocks = structuredClone(blocks) as BaseBlock;
    const childrenProp: unknown = getPathValue(newBlocks, path);
    if (!isChildrenProp(childrenProp)) return;

    const insertIndex = index ?? childrenProp.value.length;
    const blocksArray = Array.isArray(block) ? block : [block];

    const clonedBlocks = blocksArray.map(
      block => cloneWithNewIDs(block) as BaseBlock,
    );
    childrenProp.value.splice(insertIndex, 0, ...clonedBlocks);

    const newBlockPath = `blocks.${path}.value.${insertIndex}`;
    updateBlocksFromCurrentPage(newBlocks);

    set(
      {
        selectedBlockPath: newBlockPath,
      },
      false,
      "blocks/addBlocks",
    );
  },

  addBlocksToRoot: (
    block: BaseBlock | BaseBlock[],
    rootPath = "props.children",
  ) => {
    const { addBlocks } = get();
    addBlocks(block, rootPath);
  },

  setSelectedBlockPath: path =>
    set({ selectedBlockPath: path }, undefined, "blocks/setSelectedBlockPath"),

  setDraggingTemplate: template =>
    set(
      { draggingTemplate: template },
      undefined,
      "blocks/setDraggingTemplate",
    ),

  getSelectedBlock: () => {
    const { selectedBlockPath, getBlockMetaMapping } = get();
    if (!selectedBlockPath) return null;

    const block: unknown = getPathValue(
      { blocks: get().getBlocks() },
      selectedBlockPath,
      null,
    );
    if (!isBaseBlock(block)) return null;

    const blockMetaMapping = getBlockMetaMapping();
    const meta = blockMetaMapping[block.type];
    if (!meta) return null;

    return {
      block,
      meta,
    };
  },

  updateSelectedBlock: block => {
    const { selectedBlockPath, getBlocks } = get();
    const { updateBlocksFromCurrentPage } = get();

    if (!selectedBlockPath) return;

    const relativeBlockPath = selectedBlockPath.replace(/^blocks\./, "");
    const newBlocks = structuredClone(getBlocks()) as BaseBlock;
    setPathValue(newBlocks, relativeBlockPath, block);

    updateBlocksFromCurrentPage(newBlocks);
  },

  removeSelectedBlock: () => {
    const { selectedBlockPath, getBlocks } = get();
    const { updateBlocksFromCurrentPage } = get();

    if (!selectedBlockPath) return;

    const { currentIndex, relativeParentPath } =
      getBlockPathInfo(selectedBlockPath);
    if (currentIndex === -1) return;

    const newBlocks = structuredClone(getBlocks()) as BaseBlock;
    const parentArray = getParentArray(newBlocks, relativeParentPath);
    if (!parentArray) return;

    const newArray = parentArray.filter((_, index) => index !== currentIndex);
    setPathValue(newBlocks, relativeParentPath, newArray);

    set({ selectedBlockPath: null }, undefined, "blocks/removeSelectedBlock");
    updateBlocksFromCurrentPage(newBlocks);
  },

  getCanMoveSelectedBlock: direction => {
    const { selectedBlockPath } = get();
    if (!selectedBlockPath) return false;

    const { currentIndex, relativeParentPath } =
      getBlockPathInfo(selectedBlockPath);
    if (currentIndex === -1) return false;

    const parentArray = getParentArray(get().getBlocks(), relativeParentPath);
    if (!parentArray) return false;

    return direction === "up"
      ? currentIndex > 0
      : currentIndex < parentArray.length - 1;
  },

  moveSelectedBlock: direction => {
    const { selectedBlockPath, getBlocks, getCanMoveSelectedBlock } = get();
    const { updateBlocksFromCurrentPage } = get();

    if (!selectedBlockPath || !getCanMoveSelectedBlock(direction)) return;

    const { currentIndex, relativeParentPath } =
      getBlockPathInfo(selectedBlockPath);
    if (currentIndex === -1) return;

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    const newBlocks = structuredClone(getBlocks()) as BaseBlock;
    const parentArray = getParentArray(newBlocks, relativeParentPath);
    if (!parentArray) return;

    const newArray = parentArray.map((block, index) => {
      if (index === currentIndex) return parentArray[newIndex];
      if (index === newIndex) return parentArray[currentIndex];
      return block;
    });

    setPathValue(newBlocks, relativeParentPath, newArray);
    const newSelectedBlockPath = selectedBlockPath.replace(
      /\d+$/,
      newIndex.toString(),
    );

    updateBlocksFromCurrentPage(newBlocks);
    set(
      { selectedBlockPath: newSelectedBlockPath },
      undefined,
      "blocks/moveSelectedBlock",
    );
  },

  removeBlockSelection: () =>
    set({ selectedBlockPath: null }, undefined, "blocks/removeBlockSelection"),

  duplicateSelectedBlock: () => {
    const { selectedBlockPath, getBlocks } = get();
    const { updateBlocksFromCurrentPage } = get();

    if (!selectedBlockPath) {
      return;
    }

    const arrayIndexMatch = selectedBlockPath.match(/\.(\d+)$/);
    if (!arrayIndexMatch) {
      return;
    }

    const { currentIndex, relativeParentPath } =
      getBlockPathInfo(selectedBlockPath);
    const parentArrayPath = selectedBlockPath.replace(/\.\d+$/, "");
    const newBlocks = structuredClone(getBlocks()) as BaseBlock;

    const parentArray: unknown = getPathValue(newBlocks, relativeParentPath);
    if (!isBlockArray(parentArray)) {
      return;
    }

    const selectedBlock = parentArray[currentIndex];
    if (!isBaseBlock(selectedBlock)) {
      return;
    }

    const duplicatedBlock = cloneWithNewIDs(selectedBlock) as BaseBlock;
    parentArray.splice(currentIndex + 1, 0, duplicatedBlock);
    setPathValue(newBlocks, relativeParentPath, parentArray);
    const newBlockPath = `${parentArrayPath}.${currentIndex + 1}`;

    updateBlocksFromCurrentPage(newBlocks);
    set(
      {
        selectedBlockPath: newBlockPath,
      },
      undefined,
      "blocks/duplicateSelectedBlock",
    );
  },
});
